/**
 * The single recognition contract for ASCII tab string rows. Spec 2026-08-25 §6.
 *
 * Before this module there were three detectors that disagreed — `is-ascii-tab.ts`,
 * `plugins/tab/with-overrides.tsx` and `chart-parser/classify.ts` — two of which required a closing
 * `|` and all of which used a fixed `[eBGDAE]` label set, so no tab in drop-C, DADGAD, or on a 7- or
 * 8-string guitar could be recognised at all.
 */

/** Fewer rows than this is not a tab block — three dashes-and-digits lines are more likely noise. */
export const TAB_BLOCK_MIN_ROWS = 4
/** Eight-string guitar is the widest instrument we recognise. */
export const TAB_BLOCK_MAX_ROWS = 8

export interface TabBlockSpan {
  /** Inclusive index of the first string row. */
  start: number
  /** Inclusive index of the last string row. */
  end: number
}

/**
 * A tuning label: any note name with an optional accidental. Covers drop-C (`C`), drop-D (`D`),
 * DADGAD, a 7-string low `B` and an 8-string low `F#`. It is optional — some tabs give the tuning
 * once in a header and write bare rows — which is why there are two row patterns below.
 */
const LABEL_CHARS = String.raw`[A-Ga-g][#b]?`
/** `:` opens a repeated section; `-` opens a tab written without bars at all. */
const OPEN = String.raw`[|:-]`
/**
 * The em- and en-dashes are not decoration: a tab pasted through a word processor or a PDF comes
 * back with runs of `--` autocorrected to `—`, and `lib/chart-parser/src/fixtures/real-tab-and-chords.txt`
 * carries exactly that corruption on six rows. `_` is a tie/hold mark some transcribers use in place
 * of a dash. `v`/`V` is Tablang's spelling of vibrato, which `finishFor` in the parser already
 * reads. Excluding any of them splits a block at the affected row and loses the tab.
 *
 * Every letter here is a consonant, and that is load-bearing rather than incidental: a set with no
 * vowel spells no English word, so no prose line can satisfy the row patterns below however the
 * rest of the grammar is written. `o` is the first vowel admitted, and it is admitted only under
 * the adjacency rule in `REPEAT_DOT` — see there for why.
 */
const PLAIN_BODY_CHARS = String.raw`[-–—_0-9|:hHpPbBrRsSxXvV/\\~^*.()<>\[\] tT]`

/**
 * A repeat dot: the ASCII rendering of the two dots in `𝄆` and `𝄇`. Transcribers who spell the same
 * sign `|:` and `:|` were never affected, because `:` was in the alphabet from the start — which is
 * why this went unnoticed for so long. The dots fall on only the strings that carry them, typically
 * two of a bass's four, so their absence did not merely mis-parse those rows: it broke the run of
 * `TAB_BLOCK_MIN_ROWS` and the surrounding tab was rejected outright, reaching the user as "the tab
 * does not parse at all".
 *
 * Admitted only against a bar, because that is the definition of the symbol rather than a guard
 * bolted onto it: `𝄆` is a double bar FOLLOWED by dots and `𝄇` is dots followed by a double bar, so
 * a dot floating mid-row is not notation. `||o`, `o||` and `5o||` — the dot between a note and the
 * bar — are all repeat signs; `photos` is not.
 *
 * The adjacency also preserves the vowel-free property of `PLAIN_BODY_CHARS` where it matters.
 * Admitting a bare `o` makes `stop`, `prop` and `photos` spellable from the alphabet for the first
 * time, and `LABELLED_ROW` has no `HAS_EVENT` requirement to fall back on, so `G - photos` would
 * read as a string row. Measured over both fixture corpora, the loose form accepted seven such
 * prose lines and the adjacent form accepts none, while both accept every real repeat row.
 *
 * The parser ignores the character, as it does any it does not recognise, so the repeat survives in
 * `Tab.source` and nowhere else. Reading it — expanding a repeat into steps, or modelling it
 * alongside the `x4` count rows — is a separate design question.
 *
 * `𝄆`/`𝄇` themselves are deliberately NOT accepted: they are astral-plane codepoints that would
 * force the `u` flag onto these patterns, and no fixture in either corpus contains one.
 */
const REPEAT_DOT = String.raw`(?<=\|)[oO]|[oO](?=\|)`

const BODY_CHARS = String.raw`(?:${PLAIN_BODY_CHARS}|${REPEAT_DOT})`

const LABELLED_ROW = new RegExp(String.raw`^\s*${LABEL_CHARS}\s*${OPEN}${BODY_CHARS}{4,}$`)
const BARE_ROW = new RegExp(String.raw`^\s*${OPEN}${BODY_CHARS}{4,}$`)
/**
 * The discriminator that keeps markdown tables and `-----` rules out: without it `|---|---|` and
 * `--------` both match the bare shape above.
 *
 * It applies to bare rows only. Spec §6 states it over the whole grammar, but a string that rests
 * for the length of a system is written `e|----------------|` and carries no event at all — the
 * silent low E under a passage played on the top strings, or the G string of a bass line. Requiring
 * an event on every row splits such a block at the silent string and loses the tab. The label is
 * itself the evidence a bare row lacks, so the requirement is narrowed to rows that have none.
 */
const HAS_EVENT = /[0-9xX]/

export const isTabStringLine = (line: string): boolean =>
  LABELLED_ROW.test(line) || (BARE_ROW.test(line) && HAS_EVENT.test(line))

/**
 * Maximal runs of consecutive string rows, each at least `TAB_BLOCK_MIN_ROWS`. A run longer than
 * `TAB_BLOCK_MAX_ROWS` is truncated here rather than rejected.
 *
 * **The truncation is not the final span, and must not be treated as one.** `expandTabBlock`
 * deliberately completes the run before absorbing ornament rows. Two stanzas with no blank line
 * between them are one continuous run of N systems, not an N-string instrument — 10 rows is a
 * five-string bass played twice, and slicing it to 8 both loses two rows and makes the period
 * oracle read `G D A E G D A E` as a four-string bass, rendering every pitch wrong.
 *
 * So `TAB_BLOCK_MAX_ROWS` carries two meanings that happen to share a number: a span cap here, and
 * an instrument width in `stringRowWidth` and `isRenderableWidth`. Only the second is a real
 * musical limit. Removing the completion loop in `expandTabBlock` to "simplify" this reintroduces
 * the row loss — the multi-system fixtures are the guard.
 */
export const findTabBlocks = (lines: string[]): TabBlockSpan[] => {
  const blocks: TabBlockSpan[] = []
  let runStart = -1

  const closeRun = (endExclusive: number) => {
    if (runStart < 0) {
      return
    }
    const length = endExclusive - runStart
    if (length >= TAB_BLOCK_MIN_ROWS) {
      blocks.push({ start: runStart, end: runStart + Math.min(length, TAB_BLOCK_MAX_ROWS) - 1 })
    }
    runStart = -1
  }

  for (let i = 0; i < lines.length; i++) {
    if (isTabStringLine(lines[i])) {
      if (runStart < 0) {
        runStart = i
      }
      continue
    }
    closeRun(i)
  }
  closeRun(lines.length)

  return blocks
}

/**
 * Rows that belong to a tab block without being string rows. Spec 2026-08-25 §7.
 * `chart-parser/classify.ts` recognises the `x4` and `Tuning:` forms already, but has nothing for
 * palm mutes, count rows or `let ring`, and does not match the bare `Drop D` / `Capo 2` headers.
 */
const ORNAMENT_PATTERNS = [
  /^p\.?\s?m\.?/i, // PM----, P.M.
  /^(x\s?\d+|\d+\s?x|repeat\s+\d+\s?x?)$/i, // x4, 4x, Repeat 4x
  /^let\s+ring/i,
  /^hold\s+bend/i,
  /^(tuning|capo)\b/i, // Tuning: DADGAD, Capo 2 — with or without the colon
  /^(drop\s+[a-g][#b]?|dadgad|standard)$/i,
  /^[1-9ea&+\s.]+$/, // count row: 1 e & a 2 e & a
  /^[whqes\d\s.]+$/i, // duration row: w h q e
]

/**
 * An ornament row recognised by NAME — one of the forms above — and nothing else.
 *
 * Split out from `isOrnamentRow` for `isAsciiTab`, which asks a different question than
 * `expandTabBlock` does. `expandTabBlock` only ever asks about a line it already knows is flush
 * against a block, so it can afford the fuzzy ratio rule below; `isAsciiTab` asks about lines
 * anywhere in the paste, with no adjacency constraint at all, and there the ratio rule would admit
 * a chord line — `C  G  Am  F` is 4 letters in 13 characters — and silently fuse two genuinely
 * separate tabs into one. What sits BETWEEN two blocks is the whole discriminator there.
 *
 * One export used by both callers on purpose. Two copies of this list is exactly how the three
 * detectors this module replaced drifted apart.
 */
export const isNamedOrnamentRow = (line: string): boolean => {
  const trimmed = line.trim()
  if (!trimmed || isTabStringLine(line)) {
    return false
  }
  return ORNAMENT_PATTERNS.some(p => p.test(trimmed))
}

/**
 * A line that is mostly letters is prose — a lyric or an instruction — not an ornament row. The
 * threshold is the one genuinely fuzzy rule in this design; the convert-selection command is its
 * backstop.
 */
const ALPHABETIC_RATIO_MAX = 0.4

const isOrnamentRow = (line: string): boolean => {
  const trimmed = line.trim()
  if (!trimmed || isTabStringLine(line)) {
    return false
  }
  if (isNamedOrnamentRow(line)) {
    return true
  }
  const letters = trimmed.replace(/[^a-z]/gi, '').length
  return letters / trimmed.length <= ALPHABETIC_RATIO_MAX
}

/**
 * Widen a block to everything that belongs to it: first the rest of its own run of string rows,
 * then the ornament rows directly touching it. A blank line terminates the block, so only rows
 * flush against it are absorbed. Ornament rows land in `Tab.source` only — the v1 parser ignores
 * them when building `steps`.
 *
 * The run is completed first because `findTabBlocks` caps a span at `TAB_BLOCK_MAX_ROWS`, so that a
 * caller picking a single block gets a plausible instrument width rather than nothing. For capture
 * that cap is loss: the tail of a long run would fall outside the span and be handed to the song
 * tokenizer, which rewrites it. A span is the unit that gets preserved, so it has to cover the
 * whole run.
 */
export const expandTabBlock = (lines: string[], block: TabBlockSpan): TabBlockSpan => {
  let { start, end } = block
  while (start > 0 && isTabStringLine(lines[start - 1])) {
    start--
  }
  while (end < lines.length - 1 && isTabStringLine(lines[end + 1])) {
    end++
  }
  while (start > 0 && isOrnamentRow(lines[start - 1])) {
    start--
  }
  while (end < lines.length - 1 && isOrnamentRow(lines[end + 1])) {
    end++
  }
  return { start, end }
}
